/**
 * API client for the venue portal.
 * All requests go through here so we can inject the JWT token.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface MenuItem {
  id: string;
  name: string;
  category: string;
  pricePaise: number;
  available: boolean;
  createdAt: string;
}

export interface OrderItem {
  menuItemId: string;
  name: string;
  quantity: number;
  pricePaise: number;
}

export interface Order {
  id: string;
  billId: string;
  items: OrderItem[];
  totalPaise: number;
  patronPhone: string | null;
  claimToken: string | null;
  claimUrl: string | null;
  claimed: boolean;
  claimedAt: string | null;
  createdAt: string;
}

export interface CreatedOrder {
  orderId: string;
  billId: string;
  claimToken: string;
  claimUrl: string;
  totalPaise: number;
  items: OrderItem[];
}

class APIClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('venue_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('venue_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('venue_token');
  }

  private async request<T>(
    method: string,
    path: string,
    body?: any,
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Request failed',
        code: data.code,
      };
    }

    return data;
  }

  // Auth
  async register(email: string, password: string, name: string, address: string, phone: string) {
    return this.request<{ venueId: string; email: string; token: string }>('POST', '/api/auth/register', {
      email, password, name, address, phone,
    });
  }

  async onboard(data: {
    email: string;
    password: string;
    name: string;
    address: string;
    phone: string;
    menuItems: { name: string; category: string; pricePaise: number }[];
    programName: string;
    stampsRequired: number;
    rewardDescription: string;
    rewardExpiryDays: number;
  }) {
    return this.request<{ venueId: string; email: string; token: string }>('POST', '/api/auth/onboard', data);
  }

  async login(email: string, password: string) {
    return this.request<{ venueId: string; email: string; token: string }>('POST', '/api/auth/login', {
      email,
      password,
    });
  }

  async getMe() {
    return this.request<{ id: string; name: string; email: string; address: string; phone: string; logoUrl: string | null }>(
      'GET',
      '/api/auth/me',
    );
  }

  // Venue
  async getDashboard() {
    return this.request<{
      totalPatrons: number;
      newPatronsThisMonth: number;
      newPatronsLastMonth: number;
      repeatVisitors: number;
      repeatRate: number;
      repeatRateLastMonth: number;
      stampsIssuedToday: number;
      stampsThisMonth: number;
      stampsLastMonth: number;
      revenueThisMonthPaise: number;
      revenueLastMonthPaise: number;
      pendingRewards: number;
      rewardsRedeemed: number;
      hasActiveProgram: boolean;
      lapsedPatrons: number;
    }>('GET', '/api/venue/dashboard');
  }

  async exportPatrons() {
    return this.request<Array<{
      name: string;
      phone: string;
      currentStamps: number;
      totalVisits: number;
      enrolledAt: string;
    }>>('GET', '/api/patrons/export');
  }


  async updateProfile(data: { name?: string; address?: string; phone?: string; petpoojaRestaurantId?: string | null }) {
    return this.request('PUT', '/api/venue/profile', data);
  }

  // Programs
  async createProgram(data: { name: string; type: string; stampsRequired: number; rewardDescription: string; rewardExpiryDays?: number }) {
    return this.request('POST', '/api/programs', data);
  }

  async getPrograms() {
    return this.request<any[]>('GET', '/api/programs');
  }

  async updateProgram(
    id: string,
    data: { name?: string; stampsRequired?: number; rewardDescription?: string; rewardExpiryDays?: number },
  ) {
    return this.request('PUT', `/api/programs/${id}`, data);
  }

  async activateProgram(id: string) {
    return this.request<{ programs: any[] }>('PATCH', `/api/programs/${id}/activate`);
  }

  async deactivateProgram(id: string) {
    return this.request<{ programs: any[] }>('PATCH', `/api/programs/${id}/deactivate`);
  }

  // Patrons
  async getPatrons(page = 1, search = '', sort = 'joined_desc') {
    const params = new URLSearchParams({ page: String(page) });
    if (search) params.set('search', search);
    if (sort !== 'joined_desc') params.set('sort', sort);
    return this.request<{
      patrons: any[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('GET', `/api/patrons?${params}`);
  }

  async getPatron(id: string) {
    return this.request<any>('GET', `/api/patrons/${id}`);
  }

  async stampByPhone(phone: string) {
    return this.request<{
      status: 'applied' | 'pending';
      phone: string | null;
      email?: string | null;
      patronName?: string | null;
      currentStamps?: number;
      rewardGenerated?: boolean;
      stampsRequired?: number;
      message?: string;
    }>('POST', '/api/patrons/stamp-phone', { phone });
  }

  async stampByEmail(email: string) {
    return this.request<{
      status: 'applied';
      phone: string | null;
      email: string | null;
      patronName?: string | null;
      currentStamps?: number;
      rewardGenerated?: boolean;
      stampsRequired?: number;
    }>('POST', '/api/patrons/stamp-email', { email });
  }

  async getStampActivity(days = 3) {
    return this.request<Array<{
      id: string;
      createdAt: string;
      claimedAt: string | null;
      source: string;
      stampsEarned: number;
      billTotal: number;
      patronName: string | null;
      patronPhone: string | null;
      patronEmail: string | null;
      status: 'applied' | 'pending';
    }>>('GET', `/api/venue/stamp-activity?days=${days}`);
  }

  async addStamp(patronId: string) {
    return this.request<{
      currentStamps: number;
      totalVisits: number;
      rewardGenerated: boolean;
      stampsRequired: number;
    }>('POST', `/api/patrons/${patronId}/stamp`);
  }

  // Rewards
  async seedDemo() {
    return this.request<{ message: string; patronsCreated: number; patronsExisted: number }>(
      'POST', '/api/venue/seed-demo',
    );
  }

  async testBill(data: { patronPhone?: string; totalRupees?: number }) {
    return this.request('POST', '/api/venue/test-bill', data);
  }

  async getRewards(status?: string) {
    const query = status ? `?status=${status}` : '';
    return this.request<any[]>('GET', `/api/rewards${query}`);
  }

  async verifyReward(code: string) {
    return this.request<{
      id: string;
      code: string;
      patronName: string | null;
      patronPhone: string | null;
      patronEmail: string | null;
      programName: string;
      rewardDescription: string;
      status: 'PENDING' | 'REDEEMED' | 'EXPIRED';
      expiresAt: string;
      redeemedAt: string | null;
    }>('GET', `/api/rewards/${code.toUpperCase()}`);
  }

  async redeemReward(code: string) {
    return this.request('PATCH', `/api/rewards/${code}/redeem`);
  }

  // Tables
  async getTables() {
    return this.request<any[]>('GET', '/api/tables');
  }

  async createTable(tableRef: string) {
    return this.request<{ id: string; tableRef: string; qrPayload: string }>('POST', '/api/tables', { tableRef });
  }

  async deleteTable(id: string) {
    return this.request('DELETE', `/api/tables/${id}`);
  }

  // Menu
  async getMenuItems() {
    return this.request<MenuItem[]>('GET', '/api/menu');
  }

  async createMenuItem(data: { name: string; category: string; pricePaise: number }) {
    return this.request<MenuItem>('POST', '/api/menu', data);
  }

  async updateMenuItem(id: string, data: { name?: string; category?: string; pricePaise?: number; available?: boolean }) {
    return this.request<MenuItem>('PUT', `/api/menu/${id}`, data);
  }

  async toggleMenuItem(id: string) {
    return this.request<MenuItem>('PATCH', `/api/menu/${id}/toggle`);
  }

  async deleteMenuItem(id: string) {
    return this.request('DELETE', `/api/menu/${id}`);
  }

  async importMenuItems(items: { name: string; category: string; pricePaise: number }[], replace = false) {
    return this.request<{ imported: number; total: number }>('POST', '/api/menu/import', { items, replace });
  }

  // Orders
  async getOrders() {
    return this.request<Order[]>('GET', '/api/orders');
  }

  async createOrder(data: { items: OrderItem[]; totalPaise: number; patronPhone?: string }) {
    return this.request<CreatedOrder>('POST', '/api/orders', data);
  }

  // Billing
  async getBillingStatus() {
    return this.request<{
      status: string;
      trialEndsAt: string | null;
      daysRemaining: number | null;
      isDemo: boolean;
      isActive: boolean;
      isBlocked: boolean;
    }>('GET', '/api/billing/status');
  }

  // Admin
  async adminGetVenues() {
    return this.request<{
      id: string;
      name: string;
      email: string;
      phone: string;
      subscriptionStatus: string;
      trialEndsAt: string | null;
      isDemo: boolean;
      subscriptionNote: string | null;
      createdAt: string;
      daysRemaining: number | null;
      isBlocked: boolean;
      _count: { stampLedger: number; rewards: number };
    }[]>('GET', '/api/admin/venues');
  }

  async adminUpdateSubscription(venueId: string, data: {
    subscriptionStatus?: string;
    trialEndsAt?: string | null;
    subscriptionNote?: string | null;
    isDemo?: boolean;
  }) {
    return this.request<{ id: string; name: string; subscriptionStatus: string }>('PATCH', `/api/admin/venues/${venueId}/subscription`, data);
  }
}

export const api = new APIClient();
